import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box, Grid, Card, CardContent, Typography, LinearProgress, Tooltip as TipMui, Skeleton, Alert, Button, Collapse,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Chip,
} from '@mui/material';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, Filler, Tooltip, Legend,
  BarController, LineController,
} from 'chart.js';
import {
  CalendarBlank, CalendarDots, Receipt, TrendUp, Package, QrCode, UserPlus, IdentificationBadge, ArrowUp, ArrowDown,
  Wallet, ChartLineUp,
} from '@phosphor-icons/react';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, Filler, Tooltip, Legend, BarController, LineController);

// Palet sama dengan dashboard aplikasi Flutter (biru/indigo sebagai warna utama).
export const PALET = ['#3F51B5', '#1E88E5', '#26A69A', '#FFA726', '#EF5350', '#AB47BC', '#8D6E63', '#78909C'];
const HIJAU = '#66BB6A';

const rp = (n) => {
  const v = Math.round(Number(n) || 0);
  return `${v < 0 ? '-' : ''}Rp ${Math.abs(v).toLocaleString('id-ID')}`;
};
const bil = (n) => (Number(n) || 0).toLocaleString('id-ID');
const ringkas = (v) => {
  const a = Math.abs(v);
  const f = (x) => (x >= 10 ? Math.round(x) : Math.round(x * 10) / 10).toLocaleString('id-ID');
  if (a >= 1e9) return `${f(v / 1e9)} M`;
  if (a >= 1e6) return `${f(v / 1e6)} jt`;
  if (a >= 1e3) return `${f(v / 1e3)} rb`;
  return String(Math.round(v));
};
const namaBulan = (ym, panjang = false) => new Date(`${ym}-01T00:00:00`).toLocaleDateString('id-ID', { month: panjang ? 'long' : 'short', year: panjang ? 'numeric' : undefined });
const tglPanjang = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString('id-ID', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });

const opsiDasar = (rupiahY = true) => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { display: false }, tooltip: { backgroundColor: 'rgba(0,0,0,0.85)', padding: 10 } },
  scales: {
    x: { grid: { display: false }, ticks: { color: '#888', font: { size: 11 }, maxRotation: 0, autoSkipPadding: 8 } },
    y: {
      beginAtZero: true,
      grid: { color: '#eee', borderDash: [4, 4] },
      border: { display: false },
      ticks: { color: '#888', font: { size: 11 }, precision: 0, callback: (v) => (rupiahY ? ringkas(v) : v) },
    },
  },
});

const Kosong = ({ teks = 'Belum ada data pada periode ini.' }) => (
  <Box sx={{ py: 5, textAlign: 'center', color: 'text.disabled', fontSize: 14 }}>{teks}</Box>
);

export const KartuGrafik = ({ judul, sub, children, tinggi }) => (
  <Card sx={{ height: '100%', borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
    <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1, mb: 1.5 }}>
        <Typography variant="subtitle1" fontWeight={700}>{judul}</Typography>
        {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
      </Box>
      {tinggi ? <Box sx={{ height: tinggi, position: 'relative' }}>{children}</Box> : children}
    </CardContent>
  </Card>
);

export const JudulBagian = ({ children }) => (
  <Typography sx={{ mt: 4, mb: 1.5, fontSize: 12, fontWeight: 700, letterSpacing: 1, color: '#5C6BC0', textTransform: 'uppercase' }}>
    {children}
  </Typography>
);

const Banding = ({ sekarang, sebelum, label }) => {
  if (sekarang == null || sebelum == null) return null;
  if (!sebelum) return <Typography variant="caption" color="text.disabled">{label}: 0</Typography>;
  // abs: pembanding negatif (laba minus) tidak membalik arah naik/turun.
  const p = ((sekarang - sebelum) / Math.abs(sebelum)) * 100;
  const naik = p >= 0;
  const warna = naik ? 'success.main' : 'error.main';
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, color: warna }}>
      {naik ? <ArrowUp size={12} weight="bold" /> : <ArrowDown size={12} weight="bold" />}
      <Typography variant="caption" sx={{ color: warna }}>
        {Math.abs(p).toLocaleString('id-ID', { maximumFractionDigits: Math.abs(p) < 10 ? 1 : 0 })}% dari {label}
      </Typography>
    </Box>
  );
};

const KartuAngka = ({ label, nilai, ikon, warna, sekarang, sebelum, labelSebelum, keterangan }) => (
  <Card sx={{ height: '100%', borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Box sx={{ width: 30, height: 30, flexShrink: 0, borderRadius: 1.5, bgcolor: `${warna}1F`, color: warna, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {ikon}
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: 12, sm: 14 }, lineHeight: 1.25 }}>{label}</Typography>
      </Box>
      <Typography variant="h6" fontWeight={700} noWrap sx={{ fontSize: { xs: 17, sm: 20 } }}>{nilai}</Typography>
      {sekarang != null ? (
        <Banding sekarang={sekarang} sebelum={sebelum} label={labelSebelum} />
      ) : (
        <Typography variant="caption" color="text.disabled" noWrap component="div">{keterangan}</Typography>
      )}
    </CardContent>
  </Card>
);

const Peringkat = ({ data, nilai, teks, bawah, warna = PALET[0] }) => {
  if (!data?.length) return <Kosong />;
  const maks = Math.max(...data.map(nilai), 0) || 1;
  return data.map((d, i) => (
    <Box key={`${d.nama}-${i}`} sx={{ mb: 1.3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 0.4 }}>
        <Typography variant="body2" noWrap title={d.nama}>
          {d.nama}{bawah && <Typography component="span" variant="caption" color="text.secondary"> · {bawah(d)}</Typography>}
        </Typography>
        <Typography variant="body2" fontWeight={600} sx={{ whiteSpace: 'nowrap' }}>{teks(d)}</Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={(nilai(d) / maks) * 100}
        sx={{ height: 7, borderRadius: 4, bgcolor: `${warna}22`, '& .MuiLinearProgress-bar': { bgcolor: warna, borderRadius: 4 } }}
      />
    </Box>
  ));
};

const Donat = ({ data, nilai = (d) => d.omzet, label = (d) => d.label, rincian }) => {
  const total = (data || []).reduce((a, d) => a + (Number(nilai(d)) || 0), 0);
  if (!total) return <Kosong />;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <Box sx={{ width: { xs: 104, sm: 130 }, height: { xs: 104, sm: 130 }, flexShrink: 0 }}>
        <Doughnut
          data={{ labels: data.map(label), datasets: [{ data: data.map(nilai), backgroundColor: data.map((_, i) => PALET[i % PALET.length]), borderWidth: 2 }] }}
          options={{ cutout: '68%', plugins: { legend: { display: false }, tooltip: { callbacks: { label: (c) => ` ${c.label}: ${rincian ? rincian(data[c.dataIndex]) : bil(c.raw)}` } } } }}
        />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {data.map((d, i) => (
          <Box key={label(d)} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.4 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: PALET[i % PALET.length], flexShrink: 0 }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" noWrap>{label(d)}</Typography>
              {rincian && <Typography variant="caption" color="text.secondary" component="div" sx={{ lineHeight: 1.3 }}>{rincian(d)}</Typography>}
            </Box>
            <Typography variant="body2" fontWeight={600}>{Math.round((nilai(d) / total) * 100)}%</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const HARI = ['Sen', 'Sel', 'Rab', 'Kam', 'Jum', 'Sab', 'Min'];

const PetaPanas = ({ data }) => {
  const maks = Math.max(0, ...(data || []).flat());
  if (!maks) return <Kosong />;
  let awal = 24, akhir = -1;
  data.forEach((r) => r.forEach((v, j) => { if (v > 0) { awal = Math.min(awal, j); akhir = Math.max(akhir, j); } }));
  while (akhir - awal < 7) {
    if (awal > 0) awal--;
    if (akhir - awal < 7 && akhir < 23) akhir++;
  }
  const jam = Array.from({ length: akhir - awal + 1 }, (_, i) => awal + i);
  const kolom = `34px repeat(${jam.length}, 1fr)`;
  return (
    <Box>
      {data.map((r, h) => (
        <Box key={h} sx={{ display: 'grid', gridTemplateColumns: kolom, gap: '3px', mb: '3px' }}>
          <Typography variant="caption" color="text.secondary">{HARI[h]}</Typography>
          {jam.map((j) => (
            <TipMui key={j} title={`${HARI[h]} ${String(j).padStart(2, '0')}.00 · ${r[j]} transaksi`} arrow>
              <Box sx={{ height: 20, borderRadius: 0.75, bgcolor: r[j] ? PALET[0] : '#F0F2F7', opacity: r[j] ? 0.15 + (0.85 * r[j]) / maks : 1 }} />
            </TipMui>
          ))}
        </Box>
      ))}
      <Box sx={{ display: 'grid', gridTemplateColumns: kolom, gap: '3px' }}>
        <span />
        {jam.map((j, i) => (
          <Typography key={j} variant="caption" color="text.secondary" textAlign="center">
            {i % (jam.length > 12 ? 2 : 1) === 0 ? j : ''}
          </Typography>
        ))}
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 0.5, mt: 1 }}>
        <Typography variant="caption" color="text.secondary">Sepi</Typography>
        {[0.15, 0.4, 0.65, 1].map((o) => <Box key={o} sx={{ width: 14, height: 10, borderRadius: 0.5, bgcolor: PALET[0], opacity: o }} />)}
        <Typography variant="caption" color="text.secondary">Ramai</Typography>
      </Box>
    </Box>
  );
};

const LABEL_STATUS_WARNA = { settlement: '#4CAF50', success: '#4CAF50', pending: '#FFA726', cancel: '#EF5350', expire: '#EF5350', deny: '#EF5350', failure: '#EF5350' };

/** Kartu target omzet bulanan: progres, proyeksi akhir bulan, dan kebutuhan per hari. */
const KartuTarget = ({ target, bisaDiatur, namaBulan: judulBulan, aturTarget }) => {
  const [buka, setBuka] = useState(false);
  const [nilai, setNilai] = useState('');
  const [menyimpan, setMenyimpan] = useState(false);
  const [galat, setGalat] = useState('');
  const bukaDialog = () => {
    setNilai(target?.target ? String(Math.round(target.target)) : '');
    setGalat('');
    setBuka(true);
  };
  const simpan = async () => {
    setMenyimpan(true);
    setGalat('');
    try {
      await aturTarget(Number(String(nilai).replace(/\D/g, '')) || 0);
      setBuka(false);
    } catch (e) {
      setGalat(e.response?.data?.message || e.message);
    } finally {
      setMenyimpan(false);
    }
  };
  const persen = target ? Math.min(target.persen, 100) : 0;
  const tercapai = target && target.persen >= 100;
  const warna = tercapai ? '#2E7D32' : PALET[0];
  return (
    <Card sx={{ mt: 2, borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
      <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="subtitle1" fontWeight={700}>Target penjualan · {judulBulan}</Typography>
          {bisaDiatur ? (
            <Button size="small" onClick={bukaDialog}>{target ? 'Ubah target' : 'Atur target'}</Button>
          ) : (
            <Typography variant="caption" color="text.secondary">Pilih cabang untuk mengatur target</Typography>
          )}
        </Box>
        {target ? (
          <>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mt: 1, flexWrap: 'wrap' }}>
              <Typography variant="h5" fontWeight={700} sx={{ color: warna }}>
                {target.persen.toLocaleString('id-ID', { maximumFractionDigits: 1 })}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {rp(target.omzet)} dari target {rp(target.target)}
              </Typography>
            </Box>
            {/* Batang progres + penanda proyeksi akhir bulan. */}
            <Box sx={{ position: 'relative', mt: 1.5, mb: 1 }}>
              <LinearProgress
                variant="determinate"
                value={persen}
                sx={{ height: 12, borderRadius: 6, bgcolor: `${warna}22`, '& .MuiLinearProgress-bar': { bgcolor: warna, borderRadius: 6 } }}
              />
              {target.proyeksiPersen !== null && target.proyeksiPersen < 100 && (
                <TipMui title={`Proyeksi akhir bulan ${target.proyeksiPersen.toLocaleString('id-ID')}%`} arrow>
                  <Box sx={{ position: 'absolute', top: -4, bottom: -4, left: `${Math.min(target.proyeksiPersen, 100)}%`, width: 3, bgcolor: PALET[3], borderRadius: 1 }} />
                </TipMui>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {tercapai && <Chip size="small" color="success" label="Target tercapai" />}
              {target.proyeksiPersen !== null && (
                <Chip size="small" variant="outlined" label={`Proyeksi akhir bulan ${target.proyeksiPersen.toLocaleString('id-ID')}%`}
                  sx={{ borderColor: target.proyeksiPersen >= 100 ? '#2E7D32' : PALET[3], color: target.proyeksiPersen >= 100 ? '#2E7D32' : '#E65100' }} />
              )}
              {!tercapai && <Chip size="small" variant="outlined" label={`Kurang ${rp(target.sisa)}`} />}
              {target.perHari && <Chip size="small" variant="outlined" label={`Perlu ${rp(target.perHari)}/hari`} />}
            </Box>
          </>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Belum ada target untuk bulan ini. Target yang diatur berlaku untuk bulan ini dan bulan-bulan berikutnya.
          </Typography>
        )}
      </CardContent>
      <Dialog open={buka} onClose={() => !menyimpan && setBuka(false)} fullWidth maxWidth="xs">
        <DialogTitle>Target penjualan {judulBulan}</DialogTitle>
        <DialogContent sx={{ pt: '8px !important' }}>
          {galat && <Alert severity="error" sx={{ mb: 2 }}>{galat}</Alert>}
          <TextField
            autoFocus fullWidth label="Target omzet" size="small" inputMode="numeric"
            value={nilai ? Number(nilai).toLocaleString('id-ID') : ''}
            onChange={(e) => setNilai(e.target.value.replace(/\D/g, ''))}
            InputProps={{ startAdornment: <Typography sx={{ mr: 1, color: 'text.secondary' }}>Rp</Typography> }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Berlaku mulai bulan ini dan bulan-bulan berikutnya sampai diubah. Kosongkan / isi 0 untuk menghapus target.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBuka(false)} disabled={menyimpan}>Batal</Button>
          <Button variant="contained" onClick={simpan} disabled={menyimpan}>{menyimpan ? 'Menyimpan…' : 'Simpan'}</Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

const KUNCI_SEMBUNYI_MODAL = 'dashboard.sembunyiInfoModal';

/**
 * Info (bukan wajib): produk yang harga modalnya 0 membuat laba & margin terlihat lebih besar.
 * Harga modal boleh dibiarkan 0; info bisa disembunyikan dan baru muncul lagi bila jumlahnya berubah.
 */
const InfoHargaModal = ({ jumlah, daftar }) => {
  const [buka, setBuka] = useState(false);
  const [disembunyikan, setDisembunyikan] = useState(() => {
    try { return localStorage.getItem(KUNCI_SEMBUNYI_MODAL) === String(jumlah); } catch { return false; }
  });
  if (!jumlah || disembunyikan) return null;
  const sembunyikan = () => {
    try { localStorage.setItem(KUNCI_SEMBUNYI_MODAL, String(jumlah)); } catch { /* abaikan */ }
    setDisembunyikan(true);
  };
  return (
    <Alert
      severity="info"
      sx={{ mt: 2, alignItems: 'flex-start' }}
      action={(
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <Button size="small" component={RouterLink} to="/product">Isi di menu Produk</Button>
          <Button size="small" color="inherit" onClick={sembunyikan}>Sembunyikan</Button>
        </Box>
      )}
    >
      <Typography variant="body2">
        {bil(jumlah)} produk harga modalnya masih 0, jadi laba kotor, laba bersih, dan margin produk itu terlihat lebih besar.
        Harga modal boleh dibiarkan 0 (tidak wajib diisi).{' '}
        <Box component="span" sx={{ cursor: 'pointer', textDecoration: 'underline' }} onClick={() => setBuka((v) => !v)}>
          {buka ? 'Tutup daftar' : 'Lihat produknya'}
        </Box>
      </Typography>
      <Collapse in={buka}>
        <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
          {daftar.join(', ')}{jumlah > daftar.length ? `, dan ${bil(jumlah - daftar.length)} lainnya` : ''}
        </Typography>
      </Collapse>
    </Alert>
  );
};

/** Kartu ringkasan + semua grafik dashboard dari GET /dashboard/statistik. */
export const StatistikDashboard = ({ data, memuat, galat, superadmin, semuaCabang, aturTarget }) => {
  if (galat) return <Alert severity="error" sx={{ mt: 2 }}>Gagal memuat statistik: {galat}</Alert>;
  if (memuat && !data) {
    return (
      <Grid container spacing={2} sx={{ mt: 1 }}>
        {Array.from({ length: 10 }).map((_, i) => (
          <Grid item xs={6} md={4} lg={2.4} key={i}><Skeleton variant="rounded" height={104} /></Grid>
        ))}
        <Grid item xs={12}><Skeleton variant="rounded" height={300} /></Grid>
      </Grid>
    );
  }
  if (!data) return null;

  const k = data.kartu;
  const lalu = namaBulan(data.bulanan[10].bulan);
  const bln = namaBulan(data.bulan, true);
  const labelHari = data.harian.map((h) => Number(h.tanggal.slice(8)));
  const adaHarian = data.harian.some((h) => h.omzet > 0);
  const adaBulanan = data.bulanan.some((b) => b.omzet > 0);
  const adaJam = data.perJam.some((j) => j.transaksi > 0);
  const adaHadir = data.kehadiran.some((h) => h.hadir > 0);
  const adaStok = data.stok.some((s) => s.masuk > 0 || s.keluar > 0);
  const maksStatus = Math.max(1, ...data.status.map((s) => s.jumlah));

  const kartu = [
    { label: `Hari ini · ${bil(k.hariIni.transaksi)} trx`, nilai: rp(k.hariIni.omzet), ikon: <CalendarBlank size={18} />, warna: PALET[0], sekarang: k.hariIni.omzet, sebelum: k.kemarin.omzet, labelSebelum: 'kemarin' },
    { label: `${bln} · ${bil(k.bulanIni.transaksi)} trx`, nilai: rp(k.bulanIni.omzet), ikon: <CalendarDots size={18} />, warna: PALET[1], sekarang: k.bulanIni.omzet, sebelum: k.bulanLalu.omzet, labelSebelum: lalu },
    { label: 'Rata-rata per transaksi', nilai: rp(k.rataRata), ikon: <Receipt size={18} />, warna: PALET[2], sekarang: k.rataRata, sebelum: k.rataRataLalu, labelSebelum: lalu },
    { label: 'Laba kotor', nilai: rp(k.bulanIni.laba), ikon: <TrendUp size={18} />, warna: '#43A047', sekarang: k.bulanIni.laba, sebelum: k.bulanLalu.laba, labelSebelum: lalu },
    { label: 'Stok menipis', nilai: `${bil(k.stokMenipis.jumlah)} produk`, ikon: <Package size={18} />, warna: k.stokMenipis.jumlah ? '#E53935' : '#78909C', keterangan: `stok di bawah ${k.stokMenipis.batas}` },
    { label: 'QRIS menunggu bayar', nilai: bil(k.qrisMenunggu.jumlah), ikon: <QrCode size={18} />, warna: PALET[3], keterangan: rp(k.qrisMenunggu.nominal) },
    { label: 'Pelanggan baru', nilai: bil(k.pelangganBaru.jumlah), ikon: <UserPlus size={18} />, warna: PALET[5], sekarang: k.pelangganBaru.jumlah, sebelum: k.pelangganBaru.lalu, labelSebelum: lalu },
    { label: 'Laba bersih', nilai: rp(k.labaBersih), ikon: <Wallet size={18} />, warna: k.labaBersih < 0 ? '#E53935' : '#2E7D32', sekarang: k.labaBersih, sebelum: k.labaBersihLalu, labelSebelum: lalu },
    k.proyeksi
      ? { label: 'Proyeksi akhir bulan', nilai: rp(k.proyeksi.proyeksi), ikon: <ChartLineUp size={18} />, warna: PALET[1], keterangan: `hari ke-${k.proyeksi.hariBerjalan} dari ${k.proyeksi.jumlahHari}` }
      : { label: 'Beban bulan ini', nilai: rp(k.bebanBulanIni), ikon: <ChartLineUp size={18} />, warna: '#E53935', keterangan: 'pengeluaran + gaji' },
    { label: 'Karyawan hadir hari ini', nilai: `${bil(k.kehadiranHariIni.hadir)} / ${bil(k.kehadiranHariIni.total)}`, ikon: <IdentificationBadge size={18} />, warna: PALET[6], keterangan: 'kasir, dapur & karyawan' },
  ];

  return (
    <Box sx={{ opacity: memuat ? 0.6 : 1, transition: 'opacity .2s' }}>
      <JudulBagian>Ringkasan</JudulBagian>
      <Grid container spacing={2}>
        {kartu.map((c) => (
          <Grid item xs={6} md={4} lg={2.4} key={c.label}><KartuAngka {...c} /></Grid>
        ))}
      </Grid>

      <KartuTarget target={k.target} bisaDiatur={k.targetBisaDiatur} namaBulan={bln} aturTarget={aturTarget} />
      <InfoHargaModal jumlah={k.produkTanpaModal} daftar={k.produkTanpaModalDaftar || []} />

      <JudulBagian>Keuangan</JudulBagian>
      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <KartuGrafik judul="Laba bersih 12 bulan" sub="laba kotor − (pengeluaran + gaji)" tinggi={260}>
            {adaBulanan || data.bulanan.some((b) => b.pengeluaran || b.gaji) ? (
              <Bar
                data={{
                  labels: data.bulanan.map((b) => namaBulan(b.bulan)),
                  datasets: [
                    { type: 'bar', label: 'Laba kotor', data: data.bulanan.map((b) => b.laba), backgroundColor: HIJAU, borderRadius: 3, order: 2 },
                    { type: 'bar', label: 'Beban', data: data.bulanan.map((b) => b.pengeluaran + b.gaji), backgroundColor: '#EF9A9A', borderRadius: 3, order: 2 },
                    {
                      type: 'line', label: 'Laba bersih', data: data.bulanan.map((b) => b.labaBersih), borderColor: PALET[0], backgroundColor: PALET[0],
                      borderWidth: 2.5, pointRadius: 3, tension: 0, order: 1,
                    },
                  ],
                }}
                options={{
                  ...opsiDasar(),
                  scales: { ...opsiDasar().scales, y: { ...opsiDasar().scales.y, beginAtZero: true } },
                  plugins: {
                    legend: { display: true, position: 'top', align: 'start', labels: { boxWidth: 10, boxHeight: 10, font: { size: 12 } } },
                    tooltip: { ...opsiDasar().plugins.tooltip, callbacks: { title: (c) => namaBulan(data.bulanan[c[0].dataIndex].bulan, true), label: (c) => ` ${c.dataset.label}: ${rp(c.raw)}` } },
                  },
                }}
              />
            ) : <Kosong />}
          </KartuGrafik>
        </Grid>
        <Grid item xs={12} md={4}>
          <KartuGrafik judul="Pengeluaran bulan ini" sub={rp(k.bebanBulanIni)}>
            <Donat data={data.pengeluaranKategori} nilai={(d) => d.nominal} label={(d) => d.nama} rincian={(d) => rp(d.nominal)} />
            <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1 }}>
              Catat biaya di menu Laporan › Pengeluaran. Gaji diambil dari menu Gaji Karyawan.
            </Typography>
          </KartuGrafik>
        </Grid>
      </Grid>

      <JudulBagian>Penjualan</JudulBagian>
      <Grid container spacing={2}>
        <Grid item xs={12} md={7}>
          <KartuGrafik judul="Penjualan harian" sub={bln} tinggi={260}>
            {adaHarian ? (
              <Line
                data={{
                  labels: labelHari,
                  datasets: [{
                    data: data.harian.map((h) => h.omzet), borderColor: PALET[0], backgroundColor: `${PALET[0]}1F`, fill: true, tension: 0,
                    pointRadius: data.harian.map((h) => (h.omzet > 0 ? 3 : 0)), pointBackgroundColor: PALET[0], borderWidth: 2.5,
                  }],
                }}
                options={{
                  ...opsiDasar(),
                  plugins: {
                    ...opsiDasar().plugins,
                    tooltip: { ...opsiDasar().plugins.tooltip, callbacks: { title: (c) => tglPanjang(data.harian[c[0].dataIndex].tanggal), label: (c) => ` ${rp(c.raw)} · ${bil(data.harian[c.dataIndex].transaksi)} trx` } },
                  },
                }}
              />
            ) : <Kosong />}
          </KartuGrafik>
        </Grid>
        <Grid item xs={12} md={5}>
          <KartuGrafik judul="Tren 12 bulan" sub="penjualan & laba kotor" tinggi={260}>
            {adaBulanan ? (
              <Bar
                data={{
                  labels: data.bulanan.map((b) => namaBulan(b.bulan)),
                  datasets: [
                    { label: 'Penjualan', data: data.bulanan.map((b) => b.omzet), backgroundColor: PALET[0], borderRadius: 3 },
                    { label: 'Laba kotor', data: data.bulanan.map((b) => b.laba), backgroundColor: HIJAU, borderRadius: 3 },
                  ],
                }}
                options={{
                  ...opsiDasar(),
                  plugins: {
                    legend: { display: true, position: 'top', align: 'start', labels: { boxWidth: 10, boxHeight: 10, font: { size: 12 } } },
                    tooltip: { ...opsiDasar().plugins.tooltip, callbacks: { title: (c) => namaBulan(data.bulanan[c[0].dataIndex].bulan, true), label: (c) => ` ${c.dataset.label}: ${rp(c.raw)}` } },
                  },
                }}
              />
            ) : <Kosong />}
          </KartuGrafik>
        </Grid>

        <Grid item xs={12} md={6}>
          <KartuGrafik judul="Transaksi per jam" tinggi={240}>
            {adaJam ? (
              <Bar
                data={{ labels: data.perJam.map((j) => String(j.jam).padStart(2, '0')), datasets: [{ data: data.perJam.map((j) => j.transaksi), backgroundColor: PALET[0], borderRadius: 3 }] }}
                options={{
                  ...opsiDasar(false),
                  plugins: { ...opsiDasar().plugins, tooltip: { ...opsiDasar().plugins.tooltip, callbacks: { title: (c) => `Jam ${c[0].label}.00`, label: (c) => ` ${bil(c.raw)} trx · ${rp(data.perJam[c.dataIndex].omzet)}` } } },
                }}
              />
            ) : <Kosong />}
          </KartuGrafik>
        </Grid>
        <Grid item xs={12} md={6}>
          <KartuGrafik judul="Jam ramai" sub="jumlah transaksi per hari & jam">
            <PetaPanas data={data.petaPanas} />
          </KartuGrafik>
        </Grid>

        <Grid item xs={12} md={4}>
          <KartuGrafik judul="Metode pembayaran">
            <Donat data={data.metode} rincian={(d) => `${rp(d.omzet)} · ${bil(d.transaksi)} trx`} />
          </KartuGrafik>
        </Grid>
        <Grid item xs={12} md={4}>
          <KartuGrafik judul="Dine-in vs take away">
            <Donat data={data.pesanan} rincian={(d) => `${rp(d.omzet)} · ${bil(d.transaksi)} trx`} />
          </KartuGrafik>
        </Grid>
        <Grid item xs={12} md={4}>
          <KartuGrafik judul="Status transaksi" sub="berhasil, menunggu & batal">
            {data.status.length ? data.status.map((s) => (
              <Box key={s.label} sx={{ mb: 1.3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.4 }}>
                  <Typography variant="body2">{s.label} <Typography component="span" variant="caption" color="text.secondary">· {rp(s.nominal)}</Typography></Typography>
                  <Typography variant="body2" fontWeight={600}>{bil(s.jumlah)} trx</Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={(s.jumlah / maksStatus) * 100}
                  sx={{ height: 7, borderRadius: 4, bgcolor: '#eee', '& .MuiLinearProgress-bar': { bgcolor: LABEL_STATUS_WARNA[s.kode] || '#78909C', borderRadius: 4 } }}
                />
              </Box>
            )) : <Kosong />}
          </KartuGrafik>
        </Grid>

        {superadmin && semuaCabang && data.perCabang.length > 0 && (
          <Grid item xs={12}>
            <KartuGrafik judul="Perbandingan cabang" tinggi={Math.max(140, data.perCabang.length * 42)}>
              <Bar
                data={{
                  labels: data.perCabang.map((c) => c.nama),
                  datasets: [{ data: data.perCabang.map((c) => c.omzet), backgroundColor: data.perCabang.map((_, i) => PALET[i % PALET.length]), borderRadius: 4, barThickness: 22 }],
                }}
                options={{
                  ...opsiDasar(),
                  indexAxis: 'y',
                  scales: {
                    x: { ...opsiDasar().scales.y, grid: { color: '#eee' } },
                    y: { grid: { display: false }, ticks: { color: '#555', font: { size: 12 } } },
                  },
                  plugins: { ...opsiDasar().plugins, tooltip: { ...opsiDasar().plugins.tooltip, callbacks: { label: (c) => ` ${rp(c.raw)} · ${bil(data.perCabang[c.dataIndex].transaksi)} trx` } } },
                }}
              />
            </KartuGrafik>
          </Grid>
        )}
      </Grid>

      <JudulBagian>Produk</JudulBagian>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <KartuGrafik judul="Produk terlaris" sub="10 teratas">
            <Peringkat data={data.produkTerlaris} nilai={(d) => d.qty} teks={(d) => `${bil(d.qty)} terjual`} bawah={(d) => rp(d.omzet)} />
          </KartuGrafik>
        </Grid>
        <Grid item xs={12} md={4}>
          <KartuGrafik judul="Produk kurang laku" sub="paling sedikit terjual">
            {data.produkKurangLaku.length ? data.produkKurangLaku.map((p, i) => (
              <Box key={`${p.nama}-${i}`} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.75, borderBottom: '1px solid #f3f3f3' }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" noWrap>{p.nama}</Typography>
                  <Typography variant="caption" color="text.secondary">{p.kategori}</Typography>
                </Box>
                <Typography variant="body2" fontWeight={600} color={p.qty ? 'text.primary' : 'error.main'} sx={{ whiteSpace: 'nowrap' }}>
                  {p.qty ? `${bil(p.qty)} terjual` : 'tidak terjual'}
                </Typography>
              </Box>
            )) : <Kosong />}
          </KartuGrafik>
        </Grid>
        <Grid item xs={12} md={4}>
          <KartuGrafik judul="Penjualan per kategori">
            <Donat data={data.kategori} label={(d) => d.nama} rincian={(d) => `${rp(d.omzet)} · ${bil(d.qty)} item`} />
          </KartuGrafik>
        </Grid>
        <Grid item xs={12} md={6}>
          <KartuGrafik judul="Margin per produk" sub="laba kotor terbesar">
            <Peringkat
              data={data.marginProduk}
              nilai={(d) => Math.max(d.laba, 0)}
              teks={(d) => rp(d.laba)}
              bawah={(d) => (d.adaModal ? `margin ${d.margin.toLocaleString('id-ID')}%` : 'belum ada harga modal')}
              warna="#43A047"
            />
          </KartuGrafik>
        </Grid>
        <Grid item xs={12} md={6}>
          <KartuGrafik judul="Add-on / opsi terlaris" sub="topping, level, dll.">
            <Peringkat data={data.addon} nilai={(d) => d.jumlah} teks={(d) => `${bil(d.jumlah)}x`} bawah={(d) => `${d.grup}${d.nilai ? ` · ${rp(d.nilai)}` : ''}`} warna={PALET[3]} />
          </KartuGrafik>
        </Grid>
      </Grid>

      <JudulBagian>Tim & pelanggan</JudulBagian>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <KartuGrafik judul="Penjualan per kasir">
            <Peringkat data={data.kasir} nilai={(d) => d.omzet} teks={(d) => rp(d.omzet)} bawah={(d) => `${bil(d.transaksi)} trx`} warna={PALET[2]} />
          </KartuGrafik>
        </Grid>
        <Grid item xs={12} md={4}>
          <KartuGrafik judul="Pelanggan teratas">
            <Peringkat data={data.pelanggan} nilai={(d) => d.omzet} teks={(d) => rp(d.omzet)} bawah={(d) => `${bil(d.transaksi)}x`} warna={PALET[5]} />
          </KartuGrafik>
        </Grid>
        <Grid item xs={12} md={4}>
          <KartuGrafik judul="Kehadiran karyawan" sub={`${bil(data.totalKaryawan)} orang`} tinggi={220}>
            {adaHadir ? (
              <Bar
                data={{ labels: labelHari, datasets: [{ data: data.kehadiran.map((h) => h.hadir), backgroundColor: PALET[6], borderRadius: 3 }] }}
                options={{
                  ...opsiDasar(false),
                  plugins: { ...opsiDasar().plugins, tooltip: { ...opsiDasar().plugins.tooltip, callbacks: { title: (c) => tglPanjang(data.kehadiran[c[0].dataIndex].tanggal), label: (c) => ` ${c.raw} hadir` } } },
                }}
              />
            ) : <Kosong />}
          </KartuGrafik>
        </Grid>
        <Grid item xs={12} md={6}>
          <KartuGrafik judul="Pelanggan baru vs kembali" sub="pelanggan bernama">
            <Donat
              data={[
                { label: 'Baru', ...data.pelangganStatus.baru },
                { label: 'Kembali', ...data.pelangganStatus.kembali },
              ].filter((x) => x.jumlah > 0)}
              nilai={(d) => d.jumlah}
              rincian={(d) => `${bil(d.jumlah)} orang · ${rp(d.omzet)}`}
            />
          </KartuGrafik>
        </Grid>
        <Grid item xs={12} md={6}>
          <KartuGrafik judul="Item per transaksi" sub={`rata-rata ${data.itemPerTransaksi.rataRata.toLocaleString('id-ID')} item`} tinggi={200}>
            {data.itemPerTransaksi.sebaran.some((x) => x.jumlah) ? (
              <Bar
                data={{
                  labels: data.itemPerTransaksi.sebaran.map((x) => `${x.label} item`),
                  datasets: [{ data: data.itemPerTransaksi.sebaran.map((x) => x.jumlah), backgroundColor: PALET[1], borderRadius: 4, maxBarThickness: 48 }],
                }}
                options={{
                  ...opsiDasar(false),
                  plugins: { ...opsiDasar().plugins, tooltip: { ...opsiDasar().plugins.tooltip, callbacks: { label: (c) => ` ${bil(c.raw)} transaksi` } } },
                }}
              />
            ) : <Kosong />}
          </KartuGrafik>
        </Grid>
      </Grid>

      <JudulBagian>Stok</JudulBagian>
      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <KartuGrafik judul="Stok masuk vs keluar" sub={bln} tinggi={240}>
            {adaStok ? (
              <Bar
                data={{
                  labels: labelHari,
                  datasets: [
                    { label: 'Masuk', data: data.stok.map((s) => s.masuk), backgroundColor: PALET[2], borderRadius: 3 },
                    { label: 'Keluar', data: data.stok.map((s) => s.keluar), backgroundColor: PALET[4], borderRadius: 3 },
                  ],
                }}
                options={{
                  ...opsiDasar(false),
                  plugins: {
                    legend: { display: true, position: 'top', align: 'start', labels: { boxWidth: 10, boxHeight: 10, font: { size: 12 } } },
                    tooltip: { ...opsiDasar().plugins.tooltip, callbacks: { title: (c) => tglPanjang(data.stok[c[0].dataIndex].tanggal) } },
                  },
                }}
              />
            ) : <Kosong />}
          </KartuGrafik>
        </Grid>
        <Grid item xs={12} md={4}>
          <KartuGrafik judul="Stok menipis" sub={`di bawah ${k.stokMenipis.batas}`}>
            {k.stokMenipis.daftar.length ? k.stokMenipis.daftar.map((s, i) => (
              <Box key={`${s.nama}-${i}`} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.75, borderBottom: '1px solid #f3f3f3' }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" noWrap>{s.nama}</Typography>
                  {superadmin && semuaCabang && <Typography variant="caption" color="text.secondary">{s.cabang}</Typography>}
                </Box>
                <Typography variant="body2" fontWeight={700} color={s.stok <= 0 ? 'error.main' : 'warning.main'}>{bil(s.stok)}</Typography>
              </Box>
            )) : <Kosong teks="Semua stok aman." />}
          </KartuGrafik>
        </Grid>
      </Grid>
    </Box>
  );
};

export default StatistikDashboard;
